import React from 'react';
import ExternalLinkSvg from 'images/v5/icons/link-external-02.svg';
/**
 * Slack-style inline formatting for chat messages. The message text itself
 * stays a plain string end-to-end (no backend/schema changes) — these
 * markers are just characters embedded in that string, wrapped/unwrapped on
 * the way in and parsed back into React elements on the way out.
 *
 * Slack's own mrkdwn has no underline, so we invented our own marker for it
 * (double underscore, checked before the single-underscore italic pattern
 * so `__text__` isn't eaten as italic first).
 */
export const MESSAGE_FORMAT_MARKERS = {
    bold: '*',
    italic: '_',
    underline: '__',
};
// Line-prefix markers for lists — checked at the start of a line, not
// wrapped around a selection like the inline markers above. Numbered lines
// are marked with "- " rather than literal digits: the actual numbering
// comes from the rendered <ol>'s native list-style, so the raw text marker
// only needs to say "this line is part of a numbered list", not which number.
const BULLET_LINE_REGEX = /^\* (.*)$/;
const NUMBERED_LINE_REGEX = /^- (.*)$/;
// Groups, in order: url, mention, bold, underline, italic. Mention is checked
// before italic so an underscore inside a username (e.g. @jan_kowalski, which
// extractMentionsFromText's \w+ already treats as a valid mention) can't get
// swallowed as the start/end of an unrelated _italic_ span elsewhere in the text.
const FORMATTING_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+)|(@\w+)|\*([^*\n]+?)\*|__([^_\n]+?)__|_([^_\n]+?)_/g;
/**
 * Wraps the given selection in `marker` (or, for a collapsed cursor, inserts
 * an empty pair of markers with the cursor in between), returning the new
 * text and where the selection should land afterwards.
 */
export function wrapSelectionWithMarker(value, selectionStart, selectionEnd, marker) {
    const before = value.slice(0, selectionStart);
    const selected = value.slice(selectionStart, selectionEnd);
    const after = value.slice(selectionEnd);
    const nextValue = `${before}${marker}${selected}${marker}${after}`;
    const nextSelectionStart = selectionStart + marker.length;
    const nextSelectionEnd = nextSelectionStart + selected.length;
    return { value: nextValue, selectionStart: nextSelectionStart, selectionEnd: nextSelectionEnd };
}
/**
 * Prefixes every line touched by the selection with a list marker ("* " for
 * bullets, "- " for numbered — see the NUMBERED_LINE_REGEX comment above for
 * why numbered lines don't get literal digits). Unlike wrapSelectionWithMarker
 * this operates on whole lines, so it expands the given range out to the
 * start of its first line and the end of its last line first.
 */
export function applyListFormatting(value, selectionStart, selectionEnd, type) {
    const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
    const nextNewline = value.indexOf('\n', selectionEnd);
    const lineEnd = nextNewline === -1 ? value.length : nextNewline;
    const before = value.slice(0, lineStart);
    const block = value.slice(lineStart, lineEnd);
    const after = value.slice(lineEnd);
    const formattedBlock = block
        .split('\n')
        .map((line) => (type === 'bullet' ? `* ${line}` : `- ${line}`))
        .join('\n');
    // Collapse the cursor to the end of the formatted block rather than
    // selecting the whole thing — selecting from lineStart (often 0) is what
    // was making the textarea jump back to the top on every click.
    const cursorPos = lineStart + formattedBlock.length;
    return {
        value: before + formattedBlock + after,
        selectionStart: cursorPos,
        selectionEnd: cursorPos,
    };
}
/**
 * Renders a single line's inline formatting: URLs become links, @mentions get
 * wrapped in `mentionClassName` for highlighting, and the bold/italic/underline
 * markers above become their matching tags. Intentionally flat — formatting
 * isn't parsed inside other formatting — to keep this a plain regex pass
 * rather than a real markdown parser.
 */
function renderInline(text, mentionClassName) {
    const nodes = [];
    const regex = new RegExp(FORMATTING_REGEX);
    let lastIndex = 0;
    let match = regex.exec(text);
    // Each node's start offset in the line is unique, so it doubles as a stable key.
    while (match !== null) {
        if (match.index > lastIndex) {
            nodes.push(React.createElement(React.Fragment, { key: lastIndex }, text.slice(lastIndex, match.index)));
        }
        const [full, url, mention, bold, underline, italic] = match;
        if (url !== undefined) {
            const href = /^www\./.test(url) ? `https://${url}` : url;
            nodes.push(React.createElement("a", { key: match.index, href: href, target: "_blank", rel: "noopener noreferrer" },
                url,
                React.createElement(ExternalLinkSvg, { width: "12", height: "12", style: { marginLeft: 2, verticalAlign: -1 } })));
        }
        else if (mention !== undefined) {
            nodes.push(React.createElement("span", { key: match.index, className: mentionClassName }, mention));
        }
        else if (bold !== undefined) {
            nodes.push(React.createElement("strong", { key: match.index }, bold));
        }
        else if (underline !== undefined) {
            nodes.push(React.createElement("u", { key: match.index }, underline));
        }
        else if (italic !== undefined) {
            nodes.push(React.createElement("em", { key: match.index }, italic));
        }
        lastIndex = match.index + full.length;
        match = regex.exec(text);
    }
    if (lastIndex < text.length) {
        nodes.push(React.createElement(React.Fragment, { key: lastIndex }, text.slice(lastIndex)));
    }
    return nodes;
}
/**
 * Renders raw message text into React nodes, line by line: consecutive "* "
 * lines become a <ul>, consecutive "- " lines become an <ol> (numbered by the
 * browser's native list-style, not by digits in the text), and everything
 * else is plain text joined back together with <br/>. Each line's own
 * content still goes through renderInline for bold/italic/etc.
 */
export function renderFormattedMessageText(text, mentionClassName) {
    const blocks = [];
    let plainLines = [];
    let listType = null;
    let listItems = [];
    const flushPlainLines = () => {
        if (plainLines.length === 0)
            return;
        blocks.push(React.createElement(React.Fragment, { key: `p${blocks.length}` }, plainLines.map((line, index) => (React.createElement(React.Fragment, { key: index },
            index > 0 && React.createElement("br", null),
            renderInline(line, mentionClassName))))));
        plainLines = [];
    };
    const flushList = () => {
        if (listType === null)
            return;
        const ListTag = listType === 'bullet' ? 'ul' : 'ol';
        blocks.push(React.createElement(ListTag, { key: `l${blocks.length}` }, listItems));
        listType = null;
        listItems = [];
    };
    text.split('\n').forEach((line) => {
        var _a;
        const bulletMatch = BULLET_LINE_REGEX.exec(line);
        const numberedMatch = NUMBERED_LINE_REGEX.exec(line);
        let lineListType = null;
        if (bulletMatch)
            lineListType = 'bullet';
        else if (numberedMatch)
            lineListType = 'numbered';
        const itemContent = (_a = bulletMatch === null || bulletMatch === void 0 ? void 0 : bulletMatch[1]) !== null && _a !== void 0 ? _a : numberedMatch === null || numberedMatch === void 0 ? void 0 : numberedMatch[1];
        if (lineListType === null || itemContent === undefined) {
            flushList();
            plainLines.push(line);
            return;
        }
        flushPlainLines();
        if (listType !== lineListType) {
            flushList();
            listType = lineListType;
        }
        listItems.push(React.createElement("li", { key: listItems.length }, renderInline(itemContent, mentionClassName)));
    });
    flushList();
    flushPlainLines();
    return blocks;
}
